import React, { useMemo, useState } from 'react';
import {
  Banner,
  Box,
  Button,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { TableHeader } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { QuickReferenceAllIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { useQuery, useQueryClient } from 'react-query';

import ElementWithPermissions from '../../../components/ElementWithPermissions';
import { SCREENING_POLL_INTERVAL } from '../../../components/CreditCriminalStatusScreening/constants';
import useResidentScreeningsQuery from '../../../hooks/data-fetching/useResidentScreeningsQuery';
import { useScreeningMethodsByProperty } from '../../ApplicationProfile/LeaseApplicationTab/hooks.queries';
import { get } from '../../../utils/api';
import ResidentScreeningModal from './ResidentScreeningModal';
import ScreeningHistoryEntry from './ScreeningHistoryEntry';
import messages from './messages';

type Props = {
  intl: Object,
  applicationId: ?string,
  organizationId: ?string,
  propertyId: ?string,
  residentId: string,
  householdId: ?string,
  householdMembers: Array<Object>,
};

const ScreeningTab = ({
  intl,
  applicationId,
  organizationId,
  propertyId,
  residentId,
  householdId,
  householdMembers,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, refetch, isFetching, isPlaceholderData } =
    useResidentScreeningsQuery({
      organizationId: organizationId ?? '',
      propertyId: propertyId ?? '',
      residentId,
      householdId: householdId ?? '',
      options: {
        enabled: Boolean(
          organizationId && propertyId && residentId && householdId,
        ),
        refetchInterval: (queryData) => {
          const rows = queryData?.rows ?? [];
          const hasPending = rows.some((s) => {
            if (s.status === 'COMPLETED') {
              return (
                s.screeningPurpose?.name === 'Application' &&
                !s.finalDecisions?.length
              );
            }
            return !['CANCELED', 'ERROR'].includes(s.status);
          });
          return hasPending ? SCREENING_POLL_INTERVAL : false;
        },
      },
    });

  const { screeningMethods } = useScreeningMethodsByProperty({
    propertyId: propertyId ?? '',
    enabled: Boolean(propertyId && isModalOpen),
  });

  const { data: applicantsValidationResult } = useQuery(
    ['residentValidateApplicants', applicationId],
    () =>
      get(
        `/screening-service/screenings/applicants/validate/${applicationId}?isResidentScreening=true`,
      ),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(applicationId && isModalOpen),
    },
  );

  const screeningTypes = useMemo(
    () =>
      screeningMethods
        .map((m) => ({ value: m.id, text: m.method }))
        .sort((a, b) => a.text.localeCompare(b.text)),
    [screeningMethods],
  );

  const screenings = useMemo(() => data?.rows ?? [], [data]);
  const hasHistory = (screenings?.length ?? 0) > 0;
  const isInitialLoading = isFetching && isPlaceholderData;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
        <TableHeader
          sx={{
            flexGrow: 0,
            height: 'fit-content',
          }}
          title={intl.formatMessage(messages.title)}
          actions={
            <ElementWithPermissions
              scope={['resident-screening-run']}
              showTooltip
              tooltip={{
                id: 'resident-screening-run-tooltip',
                title: intl.formatMessage(messages.noPermissionTooltip),
                placement: 'top',
                arrow: true,
              }}
            >
              <Button variant={'primary'} onClick={() => setIsModalOpen(true)}>
                {intl.formatMessage(messages.submitScreening)}
              </Button>
            </ElementWithPermissions>
          }
        >
          <Banner
            status={'info'}
            message={intl.formatMessage(messages.infoBanner)}
          />
        </TableHeader>

        <Typography
          position={'sticky'}
          sx={{
            zIndex: 1,
            py: 1,
            top: 0,
            backgroundColor: 'background.paper',
            flexGrow: 0,
            height: 'fit-content',
          }}
          variant={'titleLarge'}
          display={'block'}
        >
          {intl.formatMessage(messages.historyTitle)}
        </Typography>

        <AsyncBox
          loading={isInitialLoading}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
          ChildrenContainerProps={{
            sx: {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              ...(!hasHistory && {
                backgroundColor: 'background.alt',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }),
            },
          }}
          SpinnerContainerProps={{
            sx: {
              height: 'auto',
            },
          }}
        >
          {!hasHistory && (
            <Stack
              padding={2}
              spacing={1}
              alignItems={'center'}
              justifyContent={'center'}
              sx={{ flex: 1, minHeight: 400 }}
            >
              <QuickReferenceAllIcon fontSize={'xLarge'} />
              <Typography variant={'titleLarge'} color={'text.secondary'}>
                {intl.formatMessage(messages.emptyTitle)}
              </Typography>
              <Typography variant={'bodyLarge'} color={'text.disabled'}>
                {intl.formatMessage(messages.emptyDescription)}
              </Typography>
            </Stack>
          )}

          {hasHistory &&
            screenings.map((screening) => (
              <ScreeningHistoryEntry
                key={screening.id}
                intl={intl}
                screening={screening}
              />
            ))}
        </AsyncBox>
      </Box>
      <ResidentScreeningModal
        intl={intl}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          refetch();
          queryClient.invalidateQueries([
            'residentValidateApplicants',
            applicationId,
          ]);
        }}
        applicationId={applicationId}
        organizationId={organizationId}
        propertyId={propertyId}
        screeningTypes={screeningTypes}
        householdMembers={householdMembers}
        applicantsValidationResult={applicantsValidationResult}
      />
    </>
  );
};

export default ScreeningTab;
